import React, { useEffect, useRef, useState } from 'react';
import supabase from '../../config/supabase';
import CustomModal from '../CustomModal';
import {
  fetchPurchaseOrderById,
  formatPoCurrency,
  formatPoLinePrice,
  createAmendmentDraft,
  markPurchaseOrderReceived,
  cancelPurchaseOrder,
  voidPurchaseOrder,
  archivePurchaseOrder,
  unarchivePurchaseOrder,
  createReplacementDraft,
  duplicatePurchaseOrderAsDraft,
  deleteDraftPurchaseOrder,
  sendDraftPurchaseOrder,
  updateDraftPurchaseOrder,
  computeDraftSendEligibility,
  resolveUserProfileCompactName,
  formatOrderedByLabel,
} from '../../services/purchaseOrderService';
import {
  returnPurchaseOrderToPlan,
  savingsReviewIdForPurchaseOrder,
} from '../../services/purchasingPlanService';
import { buildVendorContactInfo } from '../../utils/vendorContacts';
import { buildPoLineDisplay } from '../../utils/poLinePack';
import { getPoStatusPill, isFailureStatus } from '../../utils/poEmailStatus';
import { printPurchaseOrder } from './printPurchaseOrder';
import EmailToAnyoneModal from './EmailToAnyoneModal';
import PurchaseOrderEditor from './PurchaseOrderEditor';
import { ORDER_STATUS_STYLES, BTN_PRIMARY, BTN_SECONDARY, BTN_DANGER, DRAFT_CARD_THEME, DRAFT_PULSE_CSS, FOCUS_RING_CSS } from '../ordering/orderingStyles';
import VendorContactModal from '../ordering/VendorContactModal';

const VENDOR_CONTACT_SELECT = (
  'id, display_name, status,'
  + ' contact_name, contact_email, contact_phone,'
  + ' contact2_name, contact2_email, contact2_phone,'
  + ' general_email, general_phone,'
  + ' send_orders_to_contact_email, send_orders_to_general_email'
);

const CANCEL_REASONS = ['', 'Sent by mistake', 'Vendor unavailable', 'Delivery unavailable', 'Duplicate order', 'Other'];
const VOID_REASONS = ['', 'Entered by mistake', 'Duplicate', 'Vendor did not fulfill', 'Other'];
const FOLLOW_UP_TRIGGERS = new Set(['Vendor unavailable', 'Delivery unavailable']);

const SENT_FAMILY = [
  'sent', 'delivered', 'confirmed', 'opened', 'clicked', 'sending',
  'pending_approval', 'blocked_no_email', 'bounced', 'error', 'blocked',
];

// Use the shared status palette from the ordering module.
const STATUS_STYLES = ORDER_STATUS_STYLES;

const EVENT_TYPE_TO_STATUS = {
  'email.queued': 'sending',
  'email.sent': 'sent',
  'email.delivered': 'delivered',
  'email.opened': 'opened',
  'email.clicked': 'clicked',
  'email.bounced': 'bounced',
  'email.complained': 'complained',
  'email.error': 'error',
  'email.revised': 'revised',
};

const GREY_PILL = { bg: '#F0EDED', fg: '#9A8A84' };

function DeliveryTimeline({ summary, deliveryEvents }) {
  const rawEvents =
    (deliveryEvents && deliveryEvents.length > 0)
      ? deliveryEvents
      : (summary.purchase_order_delivery_events || []);
  const emailEvents = rawEvents
    .filter((e) => e.event_type && e.event_type.startsWith('email.'))
    .slice()
    .sort((a, b) => new Date(a.occurred_at) - new Date(b.occurred_at));

  const fallbackStatus = STATUS_STYLES[summary.delivery_status] || GREY_PILL;

  if (emailEvents.length === 0) {
    return (
      <div style={timelineStripStyle}>
        <div style={timelinePillWrapStyle}>
          <span style={timelinePillStyle(GREY_PILL)}>{fallbackStatus.label || summary.delivery_status || '—'}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={timelineStripStyle}>
      {emailEvents.map((evt, idx) => {
        const isLatest = idx === emailEvents.length - 1;
        const statusKey = EVENT_TYPE_TO_STATUS[evt.event_type];
        const style = isLatest
          ? (STATUS_STYLES[statusKey] || { bg: '#F0EDED', fg: '#9A8A84', label: evt.event_type })
          : GREY_PILL;
        const label = STATUS_STYLES[statusKey]?.label || evt.event_type.replace('email.', '') || '—';
        return (
          <div key={evt.id || `${evt.event_type}-${idx}`} style={timelinePillWrapStyle}>
            {idx > 0 && <span style={timelineArrowStyle}>›</span>}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span style={timelinePillStyle(style)}>{label}</span>
              <span style={timelineTimeStyle}>{formatDateTime(evt.occurred_at)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const timelineStripStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: 4,
  padding: '6px 16px 8px',
  borderTop: '1px solid #f5ede6',
  background: '#FDFAF8',
};
const timelinePillWrapStyle = { display: 'flex', alignItems: 'center', gap: 4 };
const timelineArrowStyle = { color: '#C8B8B0', fontSize: 14, lineHeight: 1, marginTop: -2 };
const timelinePillStyle = (s) => ({
  display: 'inline-block',
  padding: '2px 9px',
  borderRadius: 999,
  background: s.bg,
  color: s.fg,
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  lineHeight: 1.5,
});
const timelineTimeStyle = {
  fontSize: 10,
  color: '#9A8A84',
  marginTop: 2,
  textAlign: 'center',
  whiteSpace: 'nowrap',
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return String(value); }
};

const formatDeliveryDate = (v) => {
  if (!v) return null;
  try { return new Date(`${v}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return String(v); }
};

export default function PurchaseOrderCard({ summary, storeId: storeIdProp, storeName, storeAddress = '', userEmail = '', userName = '', onChanged, onOpenLinkedPo, autoExpandPoId }) {
  const isAutoExpandTarget = autoExpandPoId != null && String(summary.id) === String(autoExpandPoId);
  const [expanded, setExpanded] = useState(isAutoExpandTarget);
  const cardRef = useRef(null);
  const headerBtnRef = useRef(null);
  const didAutoScrollRef = useRef(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [amendLoading, setAmendLoading] = useState(false);
  const [receiveLoading, setReceiveLoading] = useState(false);
  const [lifecycleModal, setLifecycleModal] = useState(null);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [lifecycleError, setLifecycleError] = useState('');
  const [lifecycleReason, setLifecycleReason] = useState('');
  const [followUpModal, setFollowUpModal] = useState(false);
  const [followUpBusy, setFollowUpBusy] = useState(false);
  const [followUpError, setFollowUpError] = useState('');
  const [cardSending, setCardSending] = useState(false);
  const [cardSendConfirm, setCardSendConfirm] = useState(false);
  const [showVendorContactModal, setShowVendorContactModal] = useState(false);
  const [liveContactSnapshot, setLiveContactSnapshot] = useState(null);
  const [resolvedByName, setResolvedByName] = useState(() =>
    formatOrderedByLabel(summary.ordered_by_name, summary.ordered_by_email)
  );

  useEffect(() => {
    const staticLabel = formatOrderedByLabel(summary.ordered_by_name, summary.ordered_by_email);
    setResolvedByName(staticLabel);
    const nameIsEmail = !summary.ordered_by_name || summary.ordered_by_name.includes('@');
    if (!nameIsEmail) return;
    if (!summary.ordered_by_user_id) return;
    resolveUserProfileCompactName(summary.ordered_by_user_id).then((name) => {
      if (name) setResolvedByName(name);
    });
  }, [summary.id, summary.ordered_by_name, summary.ordered_by_email, summary.ordered_by_user_id]);

  useEffect(() => {
    if (!expanded) return;
    if (detail && detail.id === summary.id) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchPurchaseOrderById(summary.id)
      .then((data) => { if (!cancelled) setDetail(data); })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Failed to load PO details.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [expanded, summary.id, detail]);

  // When this card is the deep-link target (opened via orders.html?po=<id>),
  // scroll it into view once so the linked PO is immediately visible.
  useEffect(() => {
    if (!isAutoExpandTarget || didAutoScrollRef.current) return;
    if (!cardRef.current) return;
    didAutoScrollRef.current = true;
    try {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (_) {
      cardRef.current.scrollIntoView();
    }
  }, [isAutoExpandTarget]);

  const reloadDetail = async () => {
    setLoading(true);
    setLiveContactSnapshot(null);
    try {
      const data = await fetchPurchaseOrderById(summary.id);
      setDetail(data);
      try { window.dispatchEvent(new CustomEvent('po-failures-changed')); } catch (_e) {}
    } catch (err) {
      setError(err?.message || 'Failed to reload PO details.');
    } finally {
      setLoading(false);
    }
  };

  // When Counter Intelligence edits this order from the assistant drawer (it
  // dispatches these on a successful apply), refetch the expanded line items so
  // a changed quantity/total shows live instead of staying stale until the card
  // is collapsed and reopened. OrdersPage already reloads the SUMMARY on these
  // events; the expanded detail is its own state, so it needs its own refresh.
  // Only the expanded card pays the fetch, and only when the event targets this
  // order (events carry the edited order's poId).
  useEffect(() => {
    if (!expanded) return undefined;
    const onChanged = (e) => {
      const d = e?.detail || {};
      // 'counter-intelligence:applied' fires for every CI apply kind; only a
      // purchase-order edit can change this card's line items.
      if (e?.type === 'counter-intelligence:applied' && d.kind && d.kind !== 'po_edit') return;
      // When the event names a specific order, ignore edits to other orders.
      if (d.poId != null && String(d.poId) !== String(summary.id)) return;
      reloadDetail();
    };
    window.addEventListener('purchaseOrdersChanged', onChanged);
    window.addEventListener('counter-intelligence:applied', onChanged);
    return () => {
      window.removeEventListener('purchaseOrdersChanged', onChanged);
      window.removeEventListener('counter-intelligence:applied', onChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, summary.id]);

  // Silently resolve a stale vendor_contact_snapshot when a draft loads.
  // If the snapshot has no email but the PO has a vendor_id, fetch the live
  // vendor contact and update local state + the DB record so the Send button
  // and send-confirm dialog see the current email without any manual action.
  useEffect(() => {
    if (!detail) return;
    if (!['draft', 'blocked_no_email'].includes(detail.delivery_status)) return;
    if (!detail.vendor_id) return;
    const snap = detail.vendor_contact_snapshot || {};
    const snapHasEmail =
      (Array.isArray(snap.orderRecipientEmails) && snap.orderRecipientEmails.length > 0) ||
      !!snap.contactEmail;
    if (snapHasEmail) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error: vErr } = await supabase
          .from('vendors')
          .select(VENDOR_CONTACT_SELECT)
          .eq('id', detail.vendor_id)
          .maybeSingle();
        if (cancelled || vErr || !data) return;
        const fresh = buildVendorContactInfo(data);
        const liveHasEmail = Array.isArray(fresh.orderRecipientEmails) && fresh.orderRecipientEmails.length > 0;
        if (!liveHasEmail) return;
        const newSnapshot = { ...snap, ...fresh };
        setLiveContactSnapshot(newSnapshot);
        try {
          await updateDraftPurchaseOrder(detail.id, { vendor_contact_snapshot: newSnapshot });
        } catch (patchErr) {
          console.warn('[PurchaseOrderCard] Silent snapshot patch failed:', patchErr.message);
        }
      } catch (err) {
        console.warn('[PurchaseOrderCard] Silent vendor contact resolve failed:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [detail?.id, detail?.delivery_status, detail?.vendor_id, detail?.vendor_contact_snapshot]);

  // Leaving the preview with nothing changed. Reviewing an order is its own
  // errand — the buyer opened the card to read it, not to edit, send or delete
  // it — and until now the only way out was to scroll back up to the header.
  // A tall preview puts that header well above the viewport, so collapsing on
  // its own would leave the buyer staring at an unrelated order; scroll the
  // card back under their eyes and hand focus to the header toggle they can
  // reopen it with.
  const handleClosePreview = () => {
    setExpanded(false);
    const returnToCard = () => {
      const el = cardRef.current;
      if (!el) return;
      try {
        headerBtnRef.current?.focus({ preventScroll: true });
      } catch (_) { /* focus is a nicety, never a blocker */ }
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } catch (_) {
        if (typeof el.scrollIntoView === 'function') el.scrollIntoView();
      }
    };
    // Collapse first, scroll after the shorter card has been laid out.
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(returnToCard);
    } else {
      returnToCard();
    }
  };

  const handleAmend = async () => {
    if (!window.confirm('Create an amendment draft of this PO? You can review and edit it before sending.')) return;
    setAmendLoading(true);
    try {
      await createAmendmentDraft(summary.id);
      alert('Amendment draft created. Find it in the Ordering work queue under "Amendment Drafts".');
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      alert(`Failed to create amendment: ${err.message}`);
    }
    setAmendLoading(false);
  };

  const handleMarkReceived = async () => {
    if (!window.confirm('Mark this PO as received/delivered?')) return;
    setReceiveLoading(true);
    try {
      await markPurchaseOrderReceived(summary.id);
      await reloadDetail();
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      alert(`Failed to mark as received: ${err.message}`);
    }
    setReceiveLoading(false);
  };

  const openLifecycleModal = (type) => {
    setLifecycleReason('');
    setLifecycleError('');
    setLifecycleModal(type);
  };

  const commitLifecycle = async () => {
    setLifecycleBusy(true);
    setLifecycleError('');
    try {
      if (lifecycleModal === 'delete_draft') {
        await deleteDraftPurchaseOrder(summary.id);
        setLifecycleModal(null);
        if (typeof onChanged === 'function') onChanged();
      } else if (lifecycleModal === 'cancel_po') {
        const capturedReason = lifecycleReason;
        await cancelPurchaseOrder(summary.id, { reason: capturedReason || null });
        setLifecycleModal(null);
        await reloadDetail();
        if (typeof onChanged === 'function') onChanged();
        if (FOLLOW_UP_TRIGGERS.has(capturedReason)) setFollowUpModal(true);
      } else if (lifecycleModal === 'void_po') {
        await voidPurchaseOrder(summary.id, { reason: lifecycleReason || null });
        setLifecycleModal(null);
        await reloadDetail();
        if (typeof onChanged === 'function') onChanged();
      } else if (lifecycleModal === 'create_replacement') {
        await createReplacementDraft(summary.id);
        setLifecycleModal(null);
        await reloadDetail();
        if (typeof onChanged === 'function') onChanged();
      } else if (lifecycleModal === 'archive') {
        await archivePurchaseOrder(summary.id);
        setLifecycleModal(null);
        await reloadDetail();
        if (typeof onChanged === 'function') onChanged();
      } else if (lifecycleModal === 'unarchive') {
        await unarchivePurchaseOrder(summary.id);
        setLifecycleModal(null);
        await reloadDetail();
        if (typeof onChanged === 'function') onChanged();
      } else if (lifecycleModal === 'duplicate') {
        await duplicatePurchaseOrderAsDraft(summary.id);
        setLifecycleModal(null);
        await reloadDetail();
        if (typeof onChanged === 'function') onChanged();
      } else if (lifecycleModal === 'return_to_savings') {
        const returned = await returnPurchaseOrderToPlan(summary.id);
        setLifecycleModal(null);
        if (typeof onChanged === 'function') onChanged();
        // Returning a draft is not an end in itself — the buyer wants to
        // rearrange it. This card has just been deleted, so send them where
        // that happens rather than leaving them looking at nothing.
        if (returned?.allocationId && typeof window !== 'undefined') {
          window.location.href = `/ordering.html?savings=${encodeURIComponent(returned.allocationId)}`;
        }
      }
    } catch (err) {
      setLifecycleError(err.message || 'Action failed.');
    } finally {
      setLifecycleBusy(false);
    }
  };

  const handleCardSend = async () => {
    setCardSendConfirm(false);
    setCardSending(true);
    setError('');
    try {
      await sendDraftPurchaseOrder(summary.id, {});
      await reloadDetail();
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      setError(err.message || 'Failed to send PO.');
    } finally {
      setCardSending(false);
    }
  };

  const isArchived = !!summary.archived_at;
  const isDraftCard = ['draft', 'blocked_no_email'].includes(summary.delivery_status);
  const centralPill = getPoStatusPill(summary.delivery_status);
  const status = centralPill.bucket === 'neutral'
    ? (STATUS_STYLES[summary.delivery_status] || { bg: '#F5F5F5', fg: '#5A4A42', label: summary.delivery_status || '—' })
    : { bg: centralPill.bg, fg: centralPill.fg, label: centralPill.label };
  const showFailureDot = isFailureStatus(summary.delivery_status);
  const contact = summary.vendor_contact_snapshot || {};
  const dateLabel = formatDateTime(summary.sent_at || summary.created_at);
  const isSuperseded = summary.is_latest_revision === false || summary.delivery_status === 'revised';
  const isRevision = (summary.revision_number || 0) > 0;
  const lineCount = summary.line_count || 0;
  // What the expanded card is actually showing, for prompts that have to state
  // how many lines an action moves. Falls back to the summary's count until the
  // detail has loaded.
  const detailLineCount = (detail?.items || []).length || detail?.line_count || lineCount;

  return (
    <div ref={cardRef} style={cardStyle(expanded, isSuperseded, isDraftCard)} data-po-id={summary.id}>
      <style>{poCardMobileCss}</style>
      <button ref={headerBtnRef} type="button" onClick={() => setExpanded((v) => !v)} style={headerBtnStyle} aria-expanded={expanded}>
        <div className="po-card-header-grid" style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', textAlign: 'left' }}>
          {/* Primary: vendor name, date, creator */}
          <div>
            <div style={{ fontWeight: 700, color: '#3D2518', fontSize: 16, lineHeight: 1.2 }}>
              {summary.vendor_name_snapshot || 'Vendor'}
            </div>
            <div style={{ color: '#5A4A42', fontSize: 13, marginTop: 4 }}>{dateLabel}</div>
            <div style={{ color: '#8B7B73', fontSize: 12, marginTop: 2 }}>by {resolvedByName}</div>
          </div>
          {/* Secondary: status badge, items, total, PO number */}
          <div className="po-card-col-meta" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {showFailureDot && (
                <span
                  data-po-failure-dot="1"
                  title="Email delivery failed — resend or update the vendor address"
                  aria-label="Email delivery failed"
                  style={{
                    display: 'inline-block',
                    width: 9,
                    height: 9,
                    borderRadius: '999px',
                    background: '#E64A19',
                    boxShadow: '0 0 0 2px #fff, 0 0 0 3px rgba(230,74,25,0.35)',
                    flexShrink: 0,
                  }}
                />
              )}
              <span style={badgeStyle(status)}>{status.label}</span>
              {isRevision && (
                <span style={{ color: '#bf360c', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Rev #{summary.revision_number}
                </span>
              )}
              {summary.contact_updated_from && (() => {
                const upd = summary.contact_updated_from;
                const prevEmail = upd.previousEmail || upd.previous_email || '';
                const newEmail = (summary.vendor_contact_snapshot || {}).contactEmail || '';
                const tooltipText = prevEmail && newEmail && prevEmail !== newEmail
                  ? `Vendor email changed from ${prevEmail} to ${newEmail}`
                  : prevEmail
                    ? `Vendor contact updated (was: ${prevEmail})`
                    : 'Vendor contact was updated for this PO';
                return (
                  <span
                    title={tooltipText}
                    aria-label={tooltipText}
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: '#5A3E00',
                      background: '#FFF3CD',
                      border: '1px solid #F0C040',
                      borderRadius: 4,
                      padding: '1px 5px',
                      whiteSpace: 'nowrap',
                      cursor: 'default',
                    }}
                  >
                    Updated Contact
                  </span>
                );
              })()}
            </div>
            <div style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#3D2518', fontSize: 15 }}>
              {formatPoCurrency(summary.total)}
            </div>
            <div style={{ fontSize: 12, color: '#8B7B73' }}>
              {lineCount} item{lineCount === 1 ? '' : 's'}
              {summary.po_number ? ` · ${summary.po_number}` : ''}
            </div>
          </div>
        </div>
        <div style={{ marginLeft: 12, color: '#8B7B73', fontSize: 18, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>▾</div>
      </button>

      {/* "Review to send" call-to-action — only on collapsed draft cards */}
      {!expanded && isDraftCard && (
        <div style={{ padding: '0 16px 10px', display: 'flex' }}>
          <button
            type="button"
            className="po-review-to-send-btn"
            onClick={() => setExpanded(true)}
            aria-label="Review this draft purchase order before sending"
            style={{
              background: DRAFT_CARD_THEME.bg,
              border: `1.5px solid ${DRAFT_CARD_THEME.border}`,
              color: DRAFT_CARD_THEME.fg,
              borderRadius: 8,
              padding: '5px 14px',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
              lineHeight: 1.4,
            }}
          >
            Review to send
          </button>
        </div>
      )}

      <DeliveryTimeline summary={summary} deliveryEvents={detail?.deliveryEvents} />

      {expanded && (
        <div style={{ ...expandedStyle, borderTop: isDraftCard ? `1px solid ${DRAFT_CARD_THEME.border}` : expandedStyle.borderTop }}>
          {loading && <div style={{ color: '#8B7B73', padding: 12 }}>Loading…</div>}
          {error && <div style={{ color: '#b71c1c', background: '#fdecea', padding: 12, borderRadius: 8 }}>{error}</div>}
          {detail && !loading && (
            <>
              <div style={metaGridStyle}>
                <MetaCell label="Vendor" value={detail.vendor_name_snapshot} />
                <MetaCell label="Contact" value={contact.contactName || '—'} />
                <MetaCell label="Email" value={contact.contactEmail || '—'} />
                <MetaCell label="Phone" value={contact.contactPhone || '—'} />
                <MetaCell label="Ordered by" value={resolvedByName} />
                <MetaCell label="Sent at" value={formatDateTime(detail.sent_at || detail.created_at)} />
                <MetaCell label="PO #" value={detail.po_number} mono />
                <MetaCell label="Status" value={(STATUS_STYLES[detail.delivery_status] || status).label} />
                <MetaCell label="Delivery date" value={formatDeliveryDate(detail.requested_delivery_date) || 'Not specified'} />
                {detail.delivery_notes && <MetaCell label="Delivery notes" value={detail.delivery_notes} />}
              </div>

              {detail.notes && (
                <div style={notesStyle}><strong>Notes:</strong> {detail.notes}</div>
              )}

              <div style={{ overflowX: 'auto', border: '1px solid #E9DFD6', borderRadius: 10, marginTop: 12 }}>
                <table style={tableStyle}>
                  <thead>
                    <tr style={{ background: '#FAF6F2' }}>
                      <th style={thStyle}>Item</th>
                      {/* Pack before unit: "case" names the package, "8 bags ×
                          12 buns" is what is in it, and a receiver checking an
                          invoice needs the second one. */}
                      <th style={thStyle}>Pack / Size</th>
                      <th style={thStyle}>Count / delivery</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Vendor order</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Price / unit</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.items || []).map((it) => {
                      const d = buildPoLineDisplay(it);
                      return (
                        <tr key={it.id}>
                          <td style={tdStyle}>{it.description}</td>
                          {/* An em dash, not a guess: a line written before the
                              store had a purchase option for it has no pack on
                              record, and inventing one would misdescribe the
                              order. */}
                          <td style={tdStyle}>{d.pack || '—'}</td>
                          <td style={tdStyle}>
                            {d.countedNeed ? <div>Need {d.countedNeed}</div> : '—'}
                            {d.delivers && (
                              <div style={{ color: '#8B7B73', fontSize: 11, marginTop: 2 }}>
                                Delivers {d.delivers}{d.abovePar ? ` · ${d.abovePar} above PAR` : ''}
                              </div>
                            )}
                          </td>
                          {/* The quantity is already expressed in the vendor's
                              order unit — "1 case", never a bare "1". */}
                          <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {d.qty || '—'}
                          </td>
                          {/* NULL is "no price on record", 0 is "free". A line
                              the count could not price says so rather than
                              reading as a giveaway. */}
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatPoLinePrice(it.unit_price)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatPoLinePrice(it.line_total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#FAF6F2' }}>
                      <th colSpan={5} style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{formatPoCurrency(detail.total)}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {(detail.revisions || []).length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#5A4A42', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    Revision history
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, color: '#5A4A42', fontSize: 13 }}>
                    {detail.revisions.map((rev) => (
                      <li key={rev.id} style={{ marginBottom: 4 }}>
                        Revision #{rev.revision_number} by {rev.revised_by_email || 'Unknown'} on {formatDateTime(rev.created_at)} — added {(rev.diff?.added || []).length}, removed {(rev.diff?.removed || []).length}, changed {(rev.diff?.changed || []).length}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Replacement linkage banners */}
              {detail.replaced_by_po_id && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#EDE7F6', borderRadius: 8, fontSize: 13, color: '#4527A0', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  Replaced by{' '}
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#4527A0', textDecoration: 'underline', fontWeight: 700, fontSize: 13, fontFamily: 'inherit' }}
                    onClick={() => {
                      if (typeof onOpenLinkedPo === 'function') {
                        onOpenLinkedPo(detail.replaced_by_po_id, detail.replaced_by_po?.po_number);
                      } else {
                        const el = document.querySelector(`[data-po-id="${detail.replaced_by_po_id}"]`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }}
                  >
                    {detail.replaced_by_po?.po_number || `PO #${detail.replaced_by_po_id}`}
                  </button>
                </div>
              )}
              {detail.replacement_for_po_id && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#fff3cd', borderRadius: 8, fontSize: 13, color: '#9a6b00', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  Replacement for{' '}
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#9a6b00', textDecoration: 'underline', fontWeight: 700, fontSize: 13, fontFamily: 'inherit' }}
                    onClick={() => {
                      if (typeof onOpenLinkedPo === 'function') {
                        onOpenLinkedPo(detail.replacement_for_po_id, detail.replacement_for_po?.po_number);
                      } else {
                        const el = document.querySelector(`[data-po-id="${detail.replacement_for_po_id}"]`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }}
                  >
                    {detail.replacement_for_po?.po_number || `PO #${detail.replacement_for_po_id}`}
                  </button>
                </div>
              )}
              {detail.archived_at && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#F5F5F5', borderRadius: 8, fontSize: 13, color: '#616161' }}>
                  Archived {formatDateTime(detail.archived_at)}
                </div>
              )}
              {detail.cancellation_reason && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#FDECEA', borderRadius: 8, fontSize: 13, color: '#b71c1c' }}>
                  Cancellation reason: <strong>{detail.cancellation_reason}</strong>
                </div>
              )}
              {detail.void_reason && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#F5F5F5', borderRadius: 8, fontSize: 13, color: '#616161' }}>
                  Void reason: <strong>{detail.void_reason}</strong>
                </div>
              )}

              <div style={actionsRowStyle} className="po-card-actions-row">
                {/* Draft-like statuses: edit, generate & send, or discard */}
                {['draft', 'blocked_no_email'].includes(detail.delivery_status) && (
                  <button type="button" style={primaryActionBtnStyle} onClick={() => setShowEditor(true)}>
                    Edit Draft
                  </button>
                )}
                {['draft', 'blocked_no_email'].includes(detail.delivery_status) && (() => {
                  const { canSend, hasVendor, hasItems, hasEmail, recipients: draftRecipients, blockerReason: sendBlockReason } = computeDraftSendEligibility({
                    vendorContactSnapshot: liveContactSnapshot || detail.vendor_contact_snapshot,
                    vendorNameSnapshot: detail.vendor_name_snapshot,
                    vendorId: detail.vendor_id,
                    lineCount: detail.line_count,
                    items: detail.items,
                  });
                  const emailOnlyBlocker = hasVendor && hasItems && !hasEmail;
                  const canSendFromCard = canSend && !cardSending;
                  const canClickForEmail = emailOnlyBlocker && !cardSending;
                  const isClickable = canSendFromCard || canClickForEmail;
                  const sendBtn = (
                    <button
                      type="button"
                      style={{ ...primaryActionBtnStyle, background: isClickable ? '#2E7D32' : '#9E9E9E', borderColor: isClickable ? '#1b5e20' : '#9E9E9E', cursor: isClickable ? 'pointer' : 'not-allowed', opacity: cardSending ? 0.7 : 1 }}
                      onClick={() => {
                        if (canSendFromCard) { setCardSendConfirm(true); }
                        else if (canClickForEmail) { setShowVendorContactModal(true); }
                      }}
                      disabled={!isClickable}
                      title={emailOnlyBlocker ? 'Click to add a vendor email, then send' : (sendBlockReason || `Send this draft to ${detail.vendor_name_snapshot || 'vendor'}`)}
                    >
                      {cardSending ? 'Sending…' : 'Generate PO & Send'}
                    </button>
                  );
                  if (sendBlockReason && !canSend && !emailOnlyBlocker) {
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {sendBtn}
                        <div style={{ fontSize: 11, color: '#b71c1c', paddingLeft: 2 }}>{sendBlockReason}</div>
                      </div>
                    );
                  }
                  return sendBtn;
                })()}
                {/* A draft that came out of a Savings Review can go back into
                    it. Finishing a vendor column froze those lines; without a
                    way back, seeing a better price a minute later left the
                    buyer deleting the draft and losing the arrangement. */}
                {['draft', 'blocked_no_email'].includes(detail.delivery_status)
                  && savingsReviewIdForPurchaseOrder(detail) && (
                  <button
                    type="button"
                    data-testid="po-return-to-savings-review"
                    style={secondaryActionBtnStyle}
                    onClick={() => openLifecycleModal('return_to_savings')}
                    title="Put these items back in the Purchase Planner so they can be moved to another vendor"
                  >
                    Return to Purchase Planner
                  </button>
                )}
                {['draft', 'blocked_no_email'].includes(detail.delivery_status) && (
                  <button type="button" style={{ ...secondaryActionBtnStyle, color: '#b71c1c', borderColor: '#ef9a9a' }} onClick={() => openLifecycleModal('delete_draft')}>
                    Delete Draft
                  </button>
                )}
                {/* Sent-family: existing revision/amendment actions preserved + new lifecycle */}
                {SENT_FAMILY.includes(detail.delivery_status) && detail.delivery_status !== 'blocked_no_email' && (
                  <button type="button" style={primaryActionBtnStyle} onClick={() => setShowEditor(true)}>
                    Edit &amp; Send Revision
                  </button>
                )}
                {SENT_FAMILY.includes(detail.delivery_status) && detail.delivery_status !== 'blocked_no_email' && (
                  <button type="button" style={secondaryActionBtnStyle} onClick={handleAmend} disabled={amendLoading}>
                    {amendLoading ? 'Creating…' : 'Amend PO'}
                  </button>
                )}
                {SENT_FAMILY.includes(detail.delivery_status) && detail.delivery_status !== 'blocked_no_email' && !['delivered'].includes(detail.delivery_status) && (
                  <button type="button" style={secondaryActionBtnStyle} onClick={handleMarkReceived} disabled={receiveLoading}>
                    {receiveLoading ? 'Marking…' : 'Mark Received'}
                  </button>
                )}
                {SENT_FAMILY.includes(detail.delivery_status) && detail.delivery_status !== 'blocked_no_email' && !detail.replaced_by_po_id && (
                  <button type="button" style={{ ...secondaryActionBtnStyle, color: '#b71c1c', borderColor: '#ef9a9a' }} onClick={() => openLifecycleModal('cancel_po')}>
                    Cancel PO
                  </button>
                )}
                {SENT_FAMILY.includes(detail.delivery_status) && detail.delivery_status !== 'blocked_no_email' && !detail.replaced_by_po_id && (
                  <button type="button" style={secondaryActionBtnStyle} onClick={() => openLifecycleModal('create_replacement')}>
                    Create Replacement
                  </button>
                )}
                {SENT_FAMILY.includes(detail.delivery_status) && detail.delivery_status !== 'blocked_no_email' && (
                  <button type="button" style={secondaryActionBtnStyle} onClick={() => setShowEmailModal(true)}>
                    Email to Anyone
                  </button>
                )}
                {/* Received / Closed: Void */}
                {['received', 'closed'].includes(detail.delivery_status) && (
                  <button type="button" style={{ ...secondaryActionBtnStyle, color: '#757575' }} onClick={() => openLifecycleModal('void_po')}>
                    Void PO
                  </button>
                )}
                {/* Non-draft: Duplicate, Archive */}
                {!['draft', 'blocked_no_email'].includes(detail.delivery_status) && (
                  <button type="button" style={secondaryActionBtnStyle} onClick={() => openLifecycleModal('duplicate')}>
                    Duplicate
                  </button>
                )}
                {!['draft', 'blocked_no_email'].includes(detail.delivery_status) && (
                  <button type="button" style={secondaryActionBtnStyle} onClick={() => openLifecycleModal(isArchived ? 'unarchive' : 'archive')}>
                    {isArchived ? 'Unarchive' : 'Archive'}
                  </button>
                )}
                {/* Always available */}
                <button type="button" style={secondaryActionBtnStyle} onClick={() => printPurchaseOrder(detail, storeName, storeAddress)}>
                  Print / Save PDF
                </button>
              </div>
            </>
          )}

          {/* Sits outside the detail block on purpose: a preview that failed to
              load, or is still loading, is the one a buyer most wants out of. */}
          <div style={donePreviewRowStyle} className="po-card-done-row">
            <button
              type="button"
              data-testid="po-close-preview"
              style={donePreviewBtnStyle}
              onClick={handleClosePreview}
              title="Close this preview and go back to the orders list — nothing is changed"
              aria-label="Done reviewing this purchase order — close the preview"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Lifecycle confirm modal — uses shared CustomModal for consistent UX */}
      <CustomModal
        isOpen={!!lifecycleModal}
        onClose={() => { if (!lifecycleBusy) setLifecycleModal(null); }}
        type="custom"
        title={
          lifecycleModal === 'delete_draft' ? 'Delete this draft?' :
          lifecycleModal === 'return_to_savings' ? 'Return this draft to the Purchase Planner?' :
          lifecycleModal === 'cancel_po' ? 'Cancel this PO?' :
          lifecycleModal === 'void_po' ? 'Void this PO?' :
          lifecycleModal === 'create_replacement' ? 'Create replacement PO?' :
          lifecycleModal === 'archive' ? 'Archive this PO?' :
          lifecycleModal === 'unarchive' ? 'Unarchive this PO?' :
          'Duplicate as draft?'
        }
        message={
          lifecycleModal === 'delete_draft' ? 'This draft will be permanently deleted. This cannot be undone.' :
          lifecycleModal === 'return_to_savings' ? `This draft is removed and its ${detailLineCount} item${detailLineCount === 1 ? '' : 's'} go back to the ${detail?.vendor_name_snapshot || 'vendor'} column of the Purchase Planner, where they can be moved to another vendor. Nothing was sent, so there is nothing to withdraw — finishing that column again creates a new draft.` :
          lifecycleModal === 'cancel_po' ? 'The PO will be marked canceled and excluded from totals. This does not undo any emails sent to vendors.' :
          lifecycleModal === 'void_po' ? 'Voiding keeps the record for audit purposes but excludes it from purchase totals and reports.' :
          lifecycleModal === 'create_replacement' ? 'Creates a new editable draft copied from this PO. The original will be marked as replaced once the replacement is sent.' :
          lifecycleModal === 'archive' ? 'Archived POs are hidden from the default view but remain in your history and reports.' :
          lifecycleModal === 'unarchive' ? 'This PO will reappear in the default view.' :
          'Creates an independent draft copy with no linkage to this PO.'
        }
        messageAlign="left"
      >
        {lifecycleModal === 'cancel_po' && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#7b6b61', marginBottom: 5, textTransform: 'uppercase' }}>Reason (optional)</div>
            <select value={lifecycleReason} onChange={(e) => setLifecycleReason(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #d5c9c0', fontSize: 13, fontFamily: 'inherit' }} disabled={lifecycleBusy}>
              {CANCEL_REASONS.map((r) => <option key={r} value={r}>{r || '— No reason —'}</option>)}
            </select>
          </div>
        )}
        {lifecycleModal === 'void_po' && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#7b6b61', marginBottom: 5, textTransform: 'uppercase' }}>Reason (optional)</div>
            <select value={lifecycleReason} onChange={(e) => setLifecycleReason(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #d5c9c0', fontSize: 13, fontFamily: 'inherit' }} disabled={lifecycleBusy}>
              {VOID_REASONS.map((r) => <option key={r} value={r}>{r || '— No reason —'}</option>)}
            </select>
          </div>
        )}
        {lifecycleError && (
          <div style={{ background: '#fde8e8', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#8b0000', marginBottom: 12 }}>{lifecycleError}</div>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button style={lcCancelBtn} onClick={() => setLifecycleModal(null)} disabled={lifecycleBusy}>Cancel</button>
          <button
            style={{ ...lcConfirmBtn, background: ['cancel_po', 'void_po', 'delete_draft'].includes(lifecycleModal) ? '#b71c1c' : '#5A4A42', opacity: lifecycleBusy ? 0.6 : 1 }}
            onClick={commitLifecycle}
            disabled={lifecycleBusy}
          >
            {lifecycleBusy ? 'Working…' : (
              lifecycleModal === 'delete_draft' ? 'Delete Draft' :
              lifecycleModal === 'return_to_savings' ? 'Return to Purchase Planner' :
              lifecycleModal === 'cancel_po' ? 'Cancel PO' :
              lifecycleModal === 'void_po' ? 'Void PO' :
              lifecycleModal === 'create_replacement' ? 'Create Replacement' :
              lifecycleModal === 'archive' ? 'Archive' :
              lifecycleModal === 'unarchive' ? 'Unarchive' :
              'Duplicate as Draft'
            )}
          </button>
        </div>
      </CustomModal>

      {/* Follow-up sheet: mirrors OrderingHub FollowUpModal */}
      {followUpModal && (
        <div style={lcOverlayStyle} onClick={(e) => { if (e.target === e.currentTarget && !followUpBusy) setFollowUpModal(false); }}>
          <div style={lcBoxStyle}>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#2b1d14', marginBottom: 8 }}>What would you like to do next?</div>
            <p style={{ fontSize: 13, color: '#5A4A42', lineHeight: 1.5, margin: '0 0 14px' }}>
              The PO has been canceled. You can create a replacement order, reschedule, or close without further action.
            </p>
            {followUpError && (
              <div style={{ background: '#fde8e8', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#8b0000', marginBottom: 12 }}>{followUpError}</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                style={{ ...lcConfirmBtn, background: '#5A4A42', opacity: followUpBusy ? 0.6 : 1 }}
                disabled={followUpBusy}
                onClick={async () => {
                  setFollowUpBusy(true);
                  setFollowUpError('');
                  try {
                    await createReplacementDraft(summary.id);
                    setFollowUpModal(false);
                    if (typeof onChanged === 'function') onChanged();
                  } catch (err) {
                    setFollowUpError(err.message || 'Failed to create replacement draft.');
                  } finally {
                    setFollowUpBusy(false);
                  }
                }}
              >
                {followUpBusy ? 'Creating…' : 'Create Replacement Order'}
              </button>
              <button
                style={{ ...lcConfirmBtn, background: '#7b6b61', opacity: followUpBusy ? 0.6 : 1 }}
                disabled={followUpBusy}
                onClick={async () => {
                  setFollowUpBusy(true);
                  setFollowUpError('');
                  try {
                    // Reschedule: create linked replacement draft, then open it in the
                    // editor so the user can update the delivery date before sending.
                    const result = await createReplacementDraft(summary.id);
                    setFollowUpModal(false);
                    if (typeof onChanged === 'function') onChanged();
                    if (result?.draft_po_id) {
                      const draftData = await fetchPurchaseOrderById(result.draft_po_id);
                      if (draftData) {
                        setDetail(draftData);
                        setShowEditor(true);
                      }
                    }
                  } catch (err) {
                    setFollowUpError(err.message || 'Failed to create reschedule draft.');
                  } finally {
                    setFollowUpBusy(false);
                  }
                }}
              >
                {followUpBusy ? 'Creating…' : 'Reschedule Order'}
              </button>
              <button style={lcCancelBtn} onClick={() => setFollowUpModal(false)} disabled={followUpBusy}>
                Close Without Replacement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Card-level send confirm dialog */}
      {cardSendConfirm && detail && (() => {
        const { recipients: draftRecipients } = computeDraftSendEligibility({
          vendorContactSnapshot: liveContactSnapshot || detail.vendor_contact_snapshot,
          vendorNameSnapshot: detail.vendor_name_snapshot,
          vendorId: detail.vendor_id,
          lineCount: detail.line_count,
          items: detail.items,
        });
        const itemCount = (detail.items || []).length || (detail.line_count || 0);
        return (
          <div style={lcOverlayStyle} onClick={(e) => { if (e.target === e.currentTarget && !cardSending) setCardSendConfirm(false); }}>
            <div style={{ ...lcBoxStyle, maxWidth: 440 }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: '#2b1d14', marginBottom: 8 }}>Send this draft?</div>
              <div style={{ fontSize: 13, color: '#2b1d14', lineHeight: 1.7, marginBottom: 12 }}>
                <div><strong>Vendor:</strong> {detail.vendor_name_snapshot || '—'}</div>
                <div><strong>To:</strong> {draftRecipients.join(', ')}</div>
                <div><strong>Items:</strong> {itemCount} line{itemCount !== 1 ? 's' : ''}</div>
                <div><strong>Total:</strong> {formatPoCurrency(detail.total)}</div>
              </div>
              <div style={{ fontSize: 13, color: '#7b6b61', marginBottom: 16 }}>
                This will generate a purchase order number and email the vendor.
              </div>
              {error && <div style={{ background: '#fde8e8', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#8b0000', marginBottom: 12 }}>{error}</div>}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button style={lcCancelBtn} onClick={() => setCardSendConfirm(false)} disabled={cardSending}>Cancel</button>
                <button
                  style={{ ...lcConfirmBtn, background: '#2E7D32', opacity: cardSending ? 0.6 : 1 }}
                  onClick={handleCardSend}
                  disabled={cardSending}
                >
                  {cardSending ? 'Sending…' : 'Yes, Send PO'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showVendorContactModal && detail && (
        <VendorContactModal
          vendorId={detail.vendor_id || null}
          vendorName={detail.vendor_name_snapshot || null}
          storeId={detail.store_id || storeIdProp || null}
          onClose={() => setShowVendorContactModal(false)}
          onSaved={async (_vendorData, resolvedVendorId) => {
            setShowVendorContactModal(false);
            if (resolvedVendorId && detail && (!detail.vendor_id || detail.vendor_id !== resolvedVendorId)) {
              try {
                await updateDraftPurchaseOrder(detail.id, { vendor_id: resolvedVendorId });
              } catch (err) {
                console.warn('[PurchaseOrderCard] Could not persist resolved vendor_id:', err.message);
              }
            }
            await reloadDetail();
            setCardSendConfirm(true);
          }}
        />
      )}
      {showEmailModal && detail && (
        <EmailToAnyoneModal
          purchaseOrder={detail}
          onClose={() => setShowEmailModal(false)}
          onSent={() => { reloadDetail(); if (typeof onChanged === 'function') onChanged(); }}
        />
      )}
      {showEditor && detail && (
        <PurchaseOrderEditor
          purchaseOrder={detail}
          storeName={storeName}
          userEmail={userEmail}
          userName={userName}
          onClose={() => setShowEditor(false)}
          onSent={() => { setShowEditor(false); if (typeof onChanged === 'function') onChanged(); }}
        />
      )}
    </div>
  );
}

const MetaCell = ({ label, value, mono }) => (
  <div>
    <div style={{ fontSize: 10, fontWeight: 700, color: '#8B7B73', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    <div style={{ color: '#3D2518', fontSize: 14, marginTop: 2, fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit' }}>{value || '—'}</div>
  </div>
);

const cardStyle = (expanded, superseded, isDraft) => ({
  background: isDraft ? DRAFT_CARD_THEME.bg : '#fff',
  border: `1.5px solid ${
    isDraft
      ? (expanded ? '#C49A30' : DRAFT_CARD_THEME.border)
      : (expanded ? '#526B7C' : '#E9DFD6')
  }`,
  borderRadius: 12,
  marginBottom: 10,
  boxShadow: expanded
    ? (isDraft ? '0 6px 18px rgba(180,140,40,0.12)' : '0 6px 18px rgba(82,107,124,0.12)')
    : '0 1px 2px rgba(0,0,0,0.03)',
  opacity: superseded ? 0.78 : 1,
  transition: 'box-shadow 0.2s, border-color 0.2s',
});

const headerBtnStyle = {
  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 16px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
};
const expandedStyle = { padding: '4px 16px 16px', borderTop: '1px solid #f0e6dd' };
const metaGridStyle = { marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 };
const notesStyle = { marginTop: 12, padding: 10, background: '#FAF6F2', borderRadius: 8, color: '#3D2518', fontSize: 13 };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle = { padding: '8px 10px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#5A4A42', borderBottom: '1px solid #E9DFD6' };
const tdStyle = { padding: '6px 10px', borderBottom: '1px solid #f3ebe2', color: '#3D2518', verticalAlign: 'middle' };
const actionsRowStyle = { marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8, alignItems: 'stretch' };

// Uniform action-button base — every button in the row is the same size via the
// grid's equal-width tracks; height grows just enough for the longest two-line
// label (e.g. "Edit & Send Revision") thanks to whiteSpace:normal + lineHeight,
// with no fixed height pad. ~15px of breathing room around the text.
const UNIFORM_BTN = {
  width: '100%',
  minHeight: 0,
  padding: '15px 12px',
  boxSizing: 'border-box',
  whiteSpace: 'normal',
  textAlign: 'center',
  lineHeight: 1.25,
  fontSize: 13,
};
const primaryActionBtnStyle = { ...BTN_PRIMARY, ...UNIFORM_BTN };
const secondaryActionBtnStyle = { ...BTN_SECONDARY, ...UNIFORM_BTN };

// "Done" gets its own row under a rule rather than a cell in the actions grid:
// every button up there changes the order, and this one is the way out of the
// preview. Right-aligned on desktop, where the eye lands after the actions;
// full width on mobile, where it is the last thing under the thumb.
const donePreviewRowStyle = {
  marginTop: 14,
  paddingTop: 12,
  borderTop: '1px solid #f0e6dd',
  display: 'flex',
  justifyContent: 'flex-end',
};
const donePreviewBtnStyle = { ...BTN_SECONDARY, padding: '11px 26px' };

const lcOverlayStyle = { position: 'fixed', inset: 0, background: 'rgba(43,29,20,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)', paddingLeft: 20, paddingRight: 20 };
const lcBoxStyle = { background: '#fff', borderRadius: 14, padding: '22px', width: '100%', maxWidth: 420, boxShadow: '0 8px 40px rgba(43,29,20,0.20)', fontFamily: 'Open Sans, sans-serif' };
const lcCancelBtn = { ...BTN_SECONDARY, padding: '8px 16px', fontWeight: 600 };
const lcConfirmBtn = { ...BTN_PRIMARY, padding: '8px 16px' };
const badgeStyle = (status) => ({
  display: 'inline-block', padding: '3px 10px', borderRadius: 999,
  background: status.bg, color: status.fg, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
});

const poCardMobileCss = `
  ${FOCUS_RING_CSS}
  ${DRAFT_PULSE_CSS}
  @media (max-width: 640px) {
  .po-card-header-grid {
    grid-template-columns: 1fr !important;
    gap: 8px !important;
  }
  .po-card-col-meta {
    flex-direction: row !important;
    align-items: center !important;
    flex-wrap: wrap !important;
    gap: 8px !important;
    justify-content: flex-start !important;
  }
  .po-card-actions-row {
    grid-template-columns: repeat(3, 1fr) !important;
    gap: 6px !important;
  }
  .po-card-actions-row button {
    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    padding: 12px 6px !important;
    font-size: 12px !important;
    line-height: 1.2 !important;
  }
  .po-card-done-row button {
    width: 100% !important;
  }
}
`;
